import React from 'react'
import NextLink from 'next/link'

import { Icon } from '@wartek-id/icon'
import { Text } from '@wartek-id/text-v1'

import { assetUrl, setURLQuery } from 'utils/url'
import { useClickTracker } from 'utils/satu-event-tracker'

const HOMEPAGE_TRACKER_DEFAULT_ATTRIBUTES = {
  screen_name: 'HOMEPAGE',
}

export type MenuPanelItem = {
  title: string
  subtitle?: string
  href: string
  iconUrl: string
  hide?: boolean
  tracker?: {
    activityType: string
    keepAlive?: boolean
  }
}

export type MenuPanelProps = {
  title: string
  items: MenuPanelItem[]
}

type MenuPanelLinkProps = React.HTMLAttributes<HTMLAnchorElement> & {
  href: string
  children: React.ReactNode
}

const MenuPanelLink = ({ href, children, ...props }: MenuPanelLinkProps) => {
  const isInternalLink = href.startsWith('/')

  return isInternalLink ? (
    <NextLink href={href} passHref>
      <a {...props}>{children}</a>
    </NextLink>
  ) : (
    <a {...props} href={href}>
      {children}
    </a>
  )
}

type MenuPanelItemRowProps = {
  item: MenuPanelItem
}

const MenuPanelItemRow = ({ item }: MenuPanelItemRowProps) => {
  const trackClick = useClickTracker({
    activityType: item.tracker?.activityType ?? '',
    defaultAttributes: HOMEPAGE_TRACKER_DEFAULT_ATTRIBUTES,
    enabled: !!item.tracker,
  })

  return (
    <MenuPanelLink
      href={setURLQuery(item.href, {
        utm_source: 'rgtk',
        utm_medium: 'menu_layanan',
      })}
      className="flex items-center gap-2 border-b border-[#E5E7EB] bg-white py-4 transition-colors hover:bg-gtk-blue last:border-b-0 md:[&:nth-last-child(-n+2)]:border-b-0"
      aria-label={item.title}
      onClick={() => {
        if (item.tracker) {
          trackClick(undefined, { keepAlive: item.tracker.keepAlive })
        }
      }}
    >
      <div className="flex h-12 w-12 items-center justify-center">
        <img
          src={assetUrl(item.iconUrl)}
          alt=""
          role="presentation"
          className="h-10 w-10"
        />
      </div>
      <div className="flex-1 text-left">
        <Text variant="heading-sm" className="text-gtk-basic">
          {item.title}
        </Text>
        {!!item.subtitle && (
          <Text variant="body-sm" className="mt-1 line-clamp-2 text-[#6B7280]">
            {item.subtitle}
          </Text>
        )}
      </div>
      <Icon className="!text-gtk-blue-dark">chevron_right</Icon>
    </MenuPanelLink>
  )
}

const MenuPanel = ({ title, items }: MenuPanelProps) => {
  const visibleItems = items.filter((item) => !item.hide)

  return (
    <section className="rounded-xl bg-white shadow-md">
      <div className="px-4 py-3 md:px-6 md:py-4">
        <Text variant="heading-sm" className="text-gtk-basic">
          {title}
        </Text>
      </div>
      <div className="border-t border-[#E5E7EB]" />
      <nav aria-label={title} className="px-4 py-3 md:px-6 md:py-4">
        <div className="grid grid-cols-1 gap-y-0 gap-x-4 md:grid-cols-2 md:gap-x-20">
          {visibleItems.map((item) => (
            <MenuPanelItemRow key={`${item.title}-${item.href}`} item={item} />
          ))}
        </div>
      </nav>
    </section>
  )
}

export default MenuPanel
